/**
 * Catálogo de escenarios (fixtures versionados, felices y adversariales).
 * Cada escenario declara si requiere permiso previo y/o confirmación por ser sensible,
 * y opcionalmente un JSON Schema de negocio para validar su payload.
 */
import type { ScenarioDefinition } from "./types";

import happyPath from "./fixtures/happy-path.json";
import sensitiveAction from "./fixtures/sensitive-action.json";
import invalidInput from "./fixtures/invalid-input.json";
import boundary from "./fixtures/boundary.json";
import adversarial from "./fixtures/adversarial.json";
import dependencyDown from "./fixtures/dependency-down.json";
import emptyResult from "./fixtures/empty-result.json";
import type { EngineInput } from "./types";

export const scenarios: ScenarioDefinition[] = [
  {
    id: "happy-path",
    title: "Buscar documentos (camino feliz)",
    description:
      "Ejecuta una herramienta de solo lectura con permiso previo. Termina en un resultado completo sin advertencias.",
    toolName: "search_docs",
    sensitive: false,
    requiresPermission: true,
    fixture: happyPath as EngineInput,
  },
  {
    id: "sensitive-action",
    title: "Eliminar registro (acción sensible)",
    description:
      "Herramienta irreversible: exige permiso y una confirmación explícita antes de ejecutar.",
    toolName: "delete_record",
    sensitive: true,
    requiresPermission: true,
    fixture: sensitiveAction as EngineInput,
  },
  {
    id: "invalid-input",
    title: "Entrada inválida",
    description:
      "El payload no cumple el schema de negocio de la herramienta: canal vacío y reintentos con tipo incorrecto.",
    toolName: "send_message",
    sensitive: false,
    requiresPermission: false,
    payloadValidatorName: "sendMessagePayload",
    fixture: invalidInput as EngineInput,
  },
  {
    id: "boundary",
    title: "Límite exacto (boundary)",
    description: "La lista llega exactamente al máximo soportado de elementos por lote.",
    toolName: "bulk_tag",
    sensitive: false,
    requiresPermission: false,
    fixture: boundary as EngineInput,
  },
  {
    id: "adversarial",
    title: "Contenido adversarial",
    description:
      "El payload incluye una cadena con un script embebido. Se detecta, se neutraliza y se muestra como texto plano.",
    toolName: "post_comment",
    sensitive: false,
    requiresPermission: true,
    fixture: adversarial as EngineInput,
  },
  {
    id: "dependency-down",
    title: "Adaptador externo caído",
    description:
      "Simula que el adaptador real (kill switch apagado) no responde; ofrece el modo determinista como salida.",
    toolName: "sync_crm",
    sensitive: false,
    requiresPermission: true,
    simulateDependencyDown: true,
    fixture: dependencyDown as EngineInput,
  },
  {
    id: "empty-result",
    title: "Resultado vacío",
    description: "La búsqueda se ejecuta correctamente pero no produce hallazgos que mostrar.",
    toolName: "search_docs",
    sensitive: false,
    requiresPermission: false,
    fixture: emptyResult as EngineInput,
  },
];

export const scenarioMap: Record<string, ScenarioDefinition> = Object.fromEntries(
  scenarios.map((scenario) => [scenario.id, scenario]),
);

export function getScenario(id: string): ScenarioDefinition | undefined {
  return scenarioMap[id];
}
